import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const OMNI_EDGE_LOCATION_ONBOARDING_SCRIPT_QUERY_KEY =
  'omni-edge-location-onboarding-script';

const getEdgeLocationOnboardingScriptQueryKey = (
  clusterId: string,
  edgeLocationId: string
) =>
  [
    OMNI_EDGE_LOCATION_ONBOARDING_SCRIPT_QUERY_KEY,
    clusterId,
    edgeLocationId,
  ] as const;

const getEdgeLocationOnboardingScriptQueryOptions = ({
  clusterId,
  edgeLocationId,
  enabled,
  organizationId,
}: {
  clusterId: string;
  edgeLocationId: string;
  enabled: boolean;
  organizationId: string | undefined;
}) =>
  queryOptions({
    queryKey: getEdgeLocationOnboardingScriptQueryKey(
      clusterId,
      edgeLocationId
    ),
    queryFn: async () => {
      const { data } = await apiClient.omni.getEdgeLocationOnboardingScript({
        organizationId: organizationId!,
        clusterId,
        id: edgeLocationId,
      });

      return data.script;
    },
    enabled: enabled && !!clusterId && !!edgeLocationId && !!organizationId,
  });

export const useGetEdgeLocationOnboardingScriptQuery = ({
  clusterId,
  edgeLocationId,
  enabled = true,
}: {
  clusterId: string;
  edgeLocationId: string;
  enabled?: boolean;
}) => {
  const { currentOrganization } = useOrganizations();

  const {
    data: script,
    isLoading,
    error,
    refetch,
  } = useQuery(
    getEdgeLocationOnboardingScriptQueryOptions({
      clusterId,
      edgeLocationId,
      enabled,
      organizationId: currentOrganization?.id,
    })
  );

  return {
    script,
    isLoading,
    error,
    refetch,
  };
};
